import React, { useEffect, useMemo, useState } from 'react';
import { SpotlightManager, SpotlightPulse, SpotlightTarget } from '@atlaskit/onboarding';
import {
	generateJql,
	type UseIssuesInput,
} from '@atlassian/jira-business-common/src/common/utils/get-variables/index.tsx';
import { IssueFieldStatisticsButton } from '@atlassian/jira-business-issue-field-statistics/src/ui/index.tsx';
import { isGroupItem } from '@atlassian/jira-business-list-handlers/src/common/utils/hierarchy/index.tsx';
import { ChoreographerContextProvider } from '@atlassian/jira-choreographer-services/src/ChoreographerContextProvider.tsx';
import { CoordinationClient } from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIssueFields } from '../../../../controllers/get-issue-fields/index.tsx';
import { useRankParent } from '../../../../controllers/rank-parent/index.tsx';
import { useIssuesInput } from '../../../../controllers/use-issues-input/index.tsx';

const ONBOARDING_ENGAGEMENT_ID = 'issue-field-statistics-button-m2';
const STARGATE_URL = '/gateway/api';

const IssueFieldStatisticsButtonWithPulse = ({
	displayMenuButton,
}: {
	displayMenuButton?: boolean;
}) => {
	const { getVisibleFields } = useIssueFields();
	const cloudId = useCloudId();
	const [{ rankParentIssueId }] = useRankParent();
	const [showPulse, setShowPulse] = useState(true);
	const [isMessageActive, setIsMessageActive] = useState<boolean | undefined>(undefined);
	const coordinationClient = useMemo(
		() => new CoordinationClient(cloudId, STARGATE_URL, false, '', 'engagement'),
		[cloudId],
	);

	useEffect(() => {
		coordinationClient.start(ONBOARDING_ENGAGEMENT_ID).then(setIsMessageActive);
	}, [coordinationClient]);

	const { hierarchyLevel, ...input } = useIssuesInput({
		groupIssueId: isGroupItem(rankParentIssueId) ? rankParentIssueId : undefined,
		parentIssueId: !isGroupItem(rankParentIssueId) ? rankParentIssueId : undefined,
	});

	const generateJqlInput: UseIssuesInput = {
		...input,
		// When hierarchyParent is undefined, we force flat list behaviour and calculate the jql to include all child issues
		hierarchyParent: undefined,
	};

	const handlePulseStop = () => {
		if (showPulse) {
			coordinationClient.stop?.(ONBOARDING_ENGAGEMENT_ID);
			setShowPulse(false);
		}
	};

	const jql = generateJql(generateJqlInput, input.parentIssue);

	if (displayMenuButton) {
		return (
			<IssueFieldStatisticsButton visibleFields={getVisibleFields()} jql={jql} displayMenuButton />
		);
	}

	return isMessageActive && showPulse ? (
		<ChoreographerContextProvider isChoreographed={coordinationClient.enableChoreographer}>
			<SpotlightManager>
				<SpotlightTarget name="issue-field-statistics-button">
					<SpotlightPulse radius={3} pulse={showPulse}>
						<IssueFieldStatisticsButton
							visibleFields={getVisibleFields()}
							jql={jql}
							stopPulse={handlePulseStop}
						/>
					</SpotlightPulse>
				</SpotlightTarget>
			</SpotlightManager>
		</ChoreographerContextProvider>
	) : (
		<IssueFieldStatisticsButton visibleFields={getVisibleFields()} jql={jql} />
	);
};

export default IssueFieldStatisticsButtonWithPulse;
